const GOOGLE_MAPS_SCRIPT_ID = 'rental-address-validation-google-maps';
const VALID_STATUS = 'Validated';
const ATTRIBUTE_KEYS = {
  dropoffAddress: 'Drop-off Address',
  pickupAddress: 'Pickup Address',
  dropoffDistance: 'Drop-off Distance Miles',
  pickupDistance: 'Pickup Distance Miles',
  deliveryZone: 'Delivery Zone',
  deliveryFee: 'Delivery Fee Estimate',
  status: 'Service Radius Status',
};

let googleMapsLoader;

class RentalAddressValidation extends HTMLElement {
  connectedCallback() {
    this.dropoffInput = this.querySelector('[data-rental-dropoff-address]');
    this.pickupInput = this.querySelector('[data-rental-pickup-address]');
    this.sameAddressCheckbox = this.querySelector('[data-rental-same-address]');
    this.validateButton = this.querySelector('[data-rental-validate]');
    this.statusEl = this.querySelector('[data-rental-status]');
    this.detailsEl = this.querySelector('[data-rental-details]');

    this.validateButton?.addEventListener('click', this.handleValidate);
    this.dropoffInput?.addEventListener('input', this.handleAddressInput);
    this.pickupInput?.addEventListener('input', this.handleAddressInput);
    this.sameAddressCheckbox?.addEventListener('change', this.handleSameAddressChange);
    document.addEventListener('submit', this.handleCheckoutSubmit, true);
    document.addEventListener('click', this.handleCheckoutClick, true);

    this.syncSameAddressState();
    this.syncCheckoutState(this.dataset.currentStatus === VALID_STATUS);
  }

  disconnectedCallback() {
    this.validateButton?.removeEventListener('click', this.handleValidate);
    this.dropoffInput?.removeEventListener('input', this.handleAddressInput);
    this.pickupInput?.removeEventListener('input', this.handleAddressInput);
    this.sameAddressCheckbox?.removeEventListener('change', this.handleSameAddressChange);
    document.removeEventListener('submit', this.handleCheckoutSubmit, true);
    document.removeEventListener('click', this.handleCheckoutClick, true);
  }

  handleAddressInput = () => {
    if (this.sameAddressCheckbox?.checked && document.activeElement === this.dropoffInput && this.pickupInput) {
      this.pickupInput.value = this.dropoffInput?.value || '';
    }

    this.setStatus('Validate both addresses to continue to checkout.', false);
    this.syncCheckoutState(false);
  };

  handleSameAddressChange = () => {
    this.syncSameAddressState();
    this.setStatus('Validate both addresses to continue to checkout.', false);
    this.syncCheckoutState(false);
  };

  handleValidate = async () => {
    const dropoffAddress = this.dropoffInput?.value.trim() || '';
    const pickupAddress = this.pickupInput?.value.trim() || '';
    const config = this.getConfig();

    if (!dropoffAddress || !pickupAddress) {
      this.setStatus('Please enter both drop-off and pickup addresses.', false);
      this.clearDetails();
      await this.saveAttributes({
        [ATTRIBUTE_KEYS.dropoffAddress]: dropoffAddress,
        [ATTRIBUTE_KEYS.pickupAddress]: pickupAddress,
        ...this.getClearedEstimateAttributes(),
        [ATTRIBUTE_KEYS.status]: 'Missing required rental address',
      });
      return;
    }

    if (!config.isReady) {
      this.setStatus('Rental service-area settings are incomplete. Please contact the store before checkout.', false);
      this.clearDetails();
      await this.saveAttributes({
        [ATTRIBUTE_KEYS.dropoffAddress]: dropoffAddress,
        [ATTRIBUTE_KEYS.pickupAddress]: pickupAddress,
        ...this.getClearedEstimateAttributes(),
        [ATTRIBUTE_KEYS.status]: 'Service area settings incomplete',
      });
      return;
    }

    this.setLoading(true);

    try {
      const [dropoffLocation, pickupLocation] = await Promise.all([
        this.geocodeAddress(dropoffAddress, config.googleApiKey),
        this.geocodeAddress(pickupAddress, config.googleApiKey),
      ]);

      const dropoffDistance = this.calculateMiles(config.serviceCenter, dropoffLocation);
      const pickupDistance = this.calculateMiles(config.serviceCenter, pickupLocation);
      const zone = this.getDeliveryZone(dropoffDistance, config.maxMiles);
      const dropoffDistanceText = this.formatMiles(dropoffDistance);
      const pickupDistanceText = this.formatMiles(pickupDistance);
      const attributes = {
        [ATTRIBUTE_KEYS.dropoffAddress]: dropoffAddress,
        [ATTRIBUTE_KEYS.pickupAddress]: pickupAddress,
        [ATTRIBUTE_KEYS.dropoffDistance]: dropoffDistanceText,
        [ATTRIBUTE_KEYS.pickupDistance]: pickupDistanceText,
        [ATTRIBUTE_KEYS.deliveryZone]: zone.label,
        [ATTRIBUTE_KEYS.deliveryFee]: zone.fee,
      };

      if (dropoffDistance > config.maxMiles || pickupDistance > config.maxMiles) {
        const outsideLabels = [];
        if (dropoffDistance > config.maxMiles) outsideLabels.push('drop-off');
        if (pickupDistance > config.maxMiles) outsideLabels.push('pickup');
        const message = `The ${outsideLabels.join(' and ')} address must be within ${config.maxMiles} miles of our service center.`;

        await this.saveAttributes({
          ...attributes,
          [ATTRIBUTE_KEYS.status]: message,
        });
        this.renderDetails(dropoffDistanceText, pickupDistanceText, zone);
        this.setStatus(message, false);
        return;
      }

      await this.saveAttributes({
        ...attributes,
        [ATTRIBUTE_KEYS.status]: VALID_STATUS,
      });
      this.renderDetails(dropoffDistanceText, pickupDistanceText, zone);
      this.setStatus(`Addresses validated. ${zone.label} delivery estimate: ${zone.fee}.`, true);
    } catch (error) {
      console.error(error);
      const message = 'We could not validate one or both addresses. Please check the addresses and try again.';

      await this.saveAttributes({
        [ATTRIBUTE_KEYS.dropoffAddress]: dropoffAddress,
        [ATTRIBUTE_KEYS.pickupAddress]: pickupAddress,
        ...this.getClearedEstimateAttributes(),
        [ATTRIBUTE_KEYS.status]: message,
      });
      this.clearDetails();
      this.setStatus(message, false);
    } finally {
      this.setLoading(false);
    }
  };

  handleCheckoutSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) return;
    if (this.isValidated()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setStatus('Please validate both rental addresses before checkout.', false);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  handleCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const checkoutButton = target.closest('[name="checkout"], .cart__checkout-button');
    if (!checkoutButton || this.isValidated()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setStatus('Please validate both rental addresses before checkout.', false);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  syncSameAddressState() {
    if (!this.pickupInput || !this.sameAddressCheckbox) return;

    if (this.sameAddressCheckbox.checked) {
      this.pickupInput.value = this.dropoffInput?.value || '';
      this.pickupInput.disabled = true;
    } else {
      this.pickupInput.disabled = false;
    }
  }

  getConfig() {
    const googleApiKey = this.dataset.googleApiKey || '';
    const lat = Number.parseFloat(this.dataset.serviceCenterLat || '');
    const lng = Number.parseFloat(this.dataset.serviceCenterLng || '');
    const maxMiles = Number.parseFloat(this.dataset.maxMiles || '45') || 45;

    return {
      googleApiKey,
      maxMiles,
      serviceCenter: { lat, lng },
      isReady: Boolean(googleApiKey && Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(maxMiles)),
    };
  }

  async geocodeAddress(address, googleApiKey) {
    await loadGoogleMaps(googleApiKey);

    const geocoder = new window.google.maps.Geocoder();
    const result = await geocoder.geocode({ address });
    const location = result.results?.[0]?.geometry?.location;

    if (!location) throw new Error(`No geocode result for ${address}`);

    return {
      lat: location.lat(),
      lng: location.lng(),
    };
  }

  calculateMiles(origin, destination) {
    const earthRadiusMiles = 3958.8;
    const lat1 = this.toRadians(origin.lat);
    const lat2 = this.toRadians(destination.lat);
    const deltaLat = this.toRadians(destination.lat - origin.lat);
    const deltaLng = this.toRadians(destination.lng - origin.lng);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusMiles * c;
  }

  getDeliveryZone(distance, maxMiles) {
    if (distance <= 15) {
      return { label: '0-15 miles', fee: 'Free' };
    }

    if (distance <= 30) {
      return { label: '15-30 miles', fee: '$45' };
    }

    if (distance <= maxMiles) {
      return { label: '30-45 miles', fee: '$60' };
    }

    return { label: 'Outside service area', fee: 'Unavailable' };
  }

  async saveAttributes(attributes) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error(`Cart attribute update failed: ${response.status}`);
    }
  }

  getClearedEstimateAttributes() {
    return {
      [ATTRIBUTE_KEYS.dropoffDistance]: '',
      [ATTRIBUTE_KEYS.pickupDistance]: '',
      [ATTRIBUTE_KEYS.deliveryZone]: '',
      [ATTRIBUTE_KEYS.deliveryFee]: '',
    };
  }

  renderDetails(dropoffDistance, pickupDistance, zone) {
    if (!this.detailsEl) return;

    this.detailsEl.innerHTML = `
      <div><dt>Drop-off distance</dt><dd>${dropoffDistance} miles</dd></div>
      <div><dt>Pickup distance</dt><dd>${pickupDistance} miles</dd></div>
      <div><dt>Delivery zone</dt><dd>${zone.label}</dd></div>
      <div><dt>Delivery fee estimate</dt><dd>${zone.fee}</dd></div>
    `;
  }

  clearDetails() {
    if (!this.detailsEl) return;

    this.detailsEl.innerHTML = '';
  }

  setStatus(message, isValid) {
    if (this.statusEl) {
      this.statusEl.textContent = message;
    }

    this.dataset.valid = isValid ? 'true' : 'false';
    this.dataset.currentStatus = isValid ? VALID_STATUS : message;
    this.syncCheckoutState(isValid);
    this.dispatchEvent(new CustomEvent('rental-address-validation:updated', { bubbles: true }));
  }

  setLoading(isLoading) {
    if (!this.validateButton) return;

    this.validateButton.disabled = isLoading;
    this.validateButton.textContent = isLoading ? 'Validating...' : 'Validate service area';
  }

  syncCheckoutState(isValid) {
    document.querySelectorAll('[name="checkout"], .cart__checkout-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = !isValid;
      button.setAttribute('aria-disabled', (!isValid).toString());
    });
  }

  isValidated() {
    return this.dataset.currentStatus === VALID_STATUS;
  }

  formatMiles(distance) {
    return (Math.round(distance * 10) / 10).toString();
  }

  toRadians(value) {
    return (value * Math.PI) / 180;
  }
}

function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.Geocoder) {
    return Promise.resolve();
  }

  if (googleMapsLoader) {
    return googleMapsLoader;
  }

  googleMapsLoader = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });

  return googleMapsLoader;
}

if (!customElements.get('rental-address-validation')) {
  customElements.define('rental-address-validation', RentalAddressValidation);
}
